import TenantPicklist from 'account/components/userManagement/tenants/TenantPicklist'
import UsersPicklist from 'account/components/userManagement/users/UsersPicklist'
import {
  CustomFilterComponentProps,
  FilterConfig,
} from 'core/components/server-side-pagination/ServerSideFilters'
import React, { useMemo } from 'react'
import FlavorPicklist from '../flavors/FlavorPickList'
import ImagePicklist from '../images/ImagePicklist'
import SimpleClusterPicklist from '../infrastructure/clusters/SimpleClusterPicklist'
import HostPicklist from '../infrastructure/hosts/HostPicklist'
import VMStatePicklist from './VMStatePicklist'

export type VMFilterParams = {
  cluster: string
  vm_state: string
  owner: string
  flavor: string
  image: string
  host?: string
  tenant_id?: string
}

/**
 * Factory function to create filter components from picklist components.
 * Adapts picklists to the CustomFilterComponentProps interface.
 */
function createFilterComponent<P extends Record<string, unknown>>(
  PicklistComponent: React.ComponentType<P>,
  filterKey: string,
  label: string,
): React.FC<CustomFilterComponentProps> {
  const FilterComponent: React.FC<CustomFilterComponentProps> = ({
    value,
    onChange,
    disabled,
    className,
  }) => (
    <PicklistComponent
      {...(({
        id: filterKey,
        name: filterKey,
        value: value || '',
        onChange,
        label,
        includeAllOption: true,
        className,
        disabled,
        compact: false,
      } as unknown) as P)}
    />
  )
  FilterComponent.displayName = `${label.replace(/\s/g, '')}Filter`
  return FilterComponent
}

const TenantFilter = createFilterComponent(TenantPicklist, 'tenant_id', 'Tenant')
const ClusterFilter = createFilterComponent(SimpleClusterPicklist, 'cluster', 'Cluster')
const VMStateFilter = createFilterComponent(VMStatePicklist, 'vm_state', 'VM State')
const OwnerFilter = createFilterComponent(UsersPicklist, 'owner', 'Owner')
const FlavorFilter = createFilterComponent(FlavorPicklist, 'flavor', 'Flavor')
const ImageFilter = createFilterComponent(ImagePicklist, 'image', 'Image')
const HostFilter = createFilterComponent(HostPicklist, 'host', 'Host')

interface UseVMFilterConfigsOptions {
  allTenants: boolean
  excludeFilters?: string[]
  isAdmin: boolean
}

export const useVMFilterConfigs = ({
  allTenants,
  excludeFilters = [],
  isAdmin,
}: UseVMFilterConfigsOptions): FilterConfig[] => {
  return useMemo(
    () => [
      {
        key: 'tenant_id',
        label: 'Tenant',
        customComponent: TenantFilter,
        defaultValue: '',
        visible: allTenants && !excludeFilters.includes('tenant_id'),
      },
      {
        key: 'cluster',
        label: 'Cluster',
        customComponent: ClusterFilter,
        defaultValue: 'all',
        visible: !excludeFilters.includes('cluster'),
      },
      {
        key: 'vm_state',
        label: 'VM State',
        customComponent: VMStateFilter,
        defaultValue: 'all',
        visible: !excludeFilters.includes('vm_state'),
      },
      {
        key: 'owner',
        label: 'Owner',
        customComponent: OwnerFilter,
        defaultValue: 'all',
        visible: isAdmin && !excludeFilters.includes('owner'),
      },
      {
        key: 'flavor',
        label: 'Flavor',
        customComponent: FlavorFilter,
        defaultValue: 'all',
        visible: !excludeFilters.includes('flavor'),
      },
      {
        key: 'image',
        label: 'Image',
        customComponent: ImageFilter,
        defaultValue: 'all',
        visible: !excludeFilters.includes('image'),
      },
      {
        key: 'host',
        label: 'Host',
        customComponent: HostFilter,
        defaultValue: 'all',
        visible: isAdmin && !excludeFilters.includes('host'),
      },
    ],
    [allTenants, excludeFilters, isAdmin],
  )
}
